import { TSelectOptionTypeUi } from '@landlord-tech/ui-kit-opp';
import { keyBy } from 'lodash';
import {
  EDateFilterOptions,
  TDynamicDateData,
} from 'data/types/general.types';
import moment from 'moment';
import { defaultDateFormat } from 'helpers/utils';
import { TFilter, TResidencyApplication } from './TableFilter.types';

export function getInitialFilterValues(
  filter: TFilter | undefined,
  query: URLSearchParams
) {
  if (!filter) return '';
  if (filter.type === 'text' || filter.type === 'select') {
    return query.get(filter.filterBy as string) || '';
  }
  if (filter.type === 'range') {
    return {
      min: query.get('minWeight') || '',
      max: query.get('maxWeight') || '',
    };
  }
  if (filter.type === 'multiSelect') {
    const filterValueMap = keyBy(filter.values, 'value');
    // const filterResidencyApplicationIdsMap = keyBy(
    //   filter.values,
    //   'residencyApplicationIds'
    // );
    let filterResult: TSelectOptionTypeUi[] = [];
    let filterOther: TSelectOptionTypeUi[] = [];
    // let filterResidencyApplicationIds: TSelectOptionTypeUi[] = [];

    if (typeof filter.filterBy === 'string') {
      filterResult = (query.get(filter.filterBy)?.split(',') || [])
        .map((value) => filterValueMap[value])
        .filter(Boolean);

      if (query.get(filter.filterByOther || '')?.length) {
        filterOther = [{ label: 'Other', value: 'other' }];
      }
    } else {
      // filterResidencyApplicationIds = (
      //   query.get(filter.filterBy[1])?.split(',') || []
      // )
      //   .map((value) => filterResidencyApplicationIdsMap[value])
      //   .filter(Boolean);

      filterResult = (query.get(filter.filterBy[0])?.split(',') || [])
        .map((value) => filterValueMap[value])
        .filter(Boolean);
    }

    return [...filterResult, ...filterOther];
  }
  if (filter.type === 'date') {
    const type = query.get(
      `${filter.filterBy}.type`
    ) as EDateFilterOptions | null;
    if (!type) return;

    let value: string[] = [];

    if (type === EDateFilterOptions.range) {
      const start = query.get(`${filter.filterBy}.start`);
      const end = query.get(`${filter.filterBy}.end`);
      if (start) value.push(moment(start).format(defaultDateFormat));
      if (end) value.push(moment(end).format(defaultDateFormat));
    } else {
      const date = query.get(`${filter.filterBy}.date`);
      if (date) value.push(moment(date).format(defaultDateFormat));
    }

    return { option: type, value } as TDynamicDateData;
  }
  return '';
}

/**
 * Converts selected options into a map of query parameters
 * based on the structure of the filter (string or array of filterBy keys).
 */
export function formatMultiSelectForQuery(
  filter: any,
  selected: TSelectOptionTypeUi[] | TSelectOptionTypeUi | null | undefined,
  query: URLSearchParams
): Record<string, string> {
  if (!Array.isArray(selected)) return {};

  const result: Record<string, string> = {};

  if (typeof filter.filterBy === 'string') {
    const mainValues = selected
      .filter((item) => item.label !== 'Other')
      .map((item) => item.value)
      .join(',');

    result[filter.filterBy] = mainValues;

    if (filter.filterByOther) {
      const hasOtherSelected =
        selected.some((item) => item.label === 'Other') ||
        !!query.get(filter.filterByOther);
      result[filter.filterByOther] =
        hasOtherSelected && selected.length ? 'true' : '';
    }
  } else if (Array.isArray(filter.filterBy)) {
    const mainValues = selected
      .map((item) => item.value)
      .filter(Boolean)
      .join(',');

    // const residencyApplicationIds = selected
    //   .map((item: TResidencyApplication) => item.residencyApplicationIds)
    //   .filter(Boolean)
    //   .join(',');

    result[filter.filterBy[0]] = mainValues;
    // result[filter.filterBy[1]] = residencyApplicationIds;
  }

  return result;
}

const DATE_FORMAT = 'YYYY-MM-DD';

export function formatDateForQuery(
  filter: TFilter,
  selected?: TDynamicDateData
): Record<string, string> {
  const result: Record<string, string> = {};
  const key = filter.filterBy as string;

  // Always clean previous fields
  result[`${key}.type`] = '';
  result[`${key}.date`] = '';
  result[`${key}.start`] = '';
  result[`${key}.end`] = '';

  if (!selected || !selected.option || !selected.value?.length) {
    return result; // Return empty cleared keys only
  }

  // Apply based on selected option
  result[`${key}.type`] = selected.option;

  if (
    selected.option === EDateFilterOptions.exact ||
    selected.option === EDateFilterOptions.before ||
    selected.option === EDateFilterOptions.after
  ) {
    result[`${key}.date`] = moment(selected.value[0]).format(DATE_FORMAT);
  }

  if (
    selected.option === EDateFilterOptions.range &&
    selected.value.length === 2
  ) {
    result[`${key}.start`] = moment(selected.value[0]).format(DATE_FORMAT);
    result[`${key}.end`] = moment(selected.value[1]).format(DATE_FORMAT);
  }

  return result;
}

export const animalRestrictionMapper = (value: string, subValues: string[]) => {
  const mapper = {
    NOT_RESTRICTED: 'Not Restricted',
    RESTRICTED: 'Restricted',
    WEIGHT: 'Weight',
    SPECIES: 'Species',
    BREED: 'Breed',
    AGE: 'Age',
    EXCEEDS_MAX_PET_NUMBER: 'Exceeds Max # of Pets',
    NO_PETS_ALLOWED: 'No Pets Allowed',
    OTHER: 'Other',
    NEED_MORE_INFORMATION: 'Need More Information',
    NOT_APPLICABLE: 'N/A',
    NOT_SPECIFIED: 'Not Specified',
  };

  if (value && value !== 'RESTRICTED') return mapper[value];
  if (subValues.length) {
    return `${mapper['RESTRICTED']}: ${subValues
      .map((i) => mapper[i])
      .join(', ')}`;
  }
  return '';
};
